import { VolunteerDetails } from '@/modals/volunteer/volunteerDetails';
import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { AppService } from '@services/app.service';
import { EncryptionService } from '@services/encryption.service';
import { NgxSpinnerService } from 'ngx-spinner';
import { ToastrService } from 'ngx-toastr';

@Component({
  selector: 'app-volunteer',
  templateUrl: './volunteer.component.html',
  styleUrls: ['./volunteer.component.scss']
})
export class VolunteerComponent implements OnInit {

  volunteers: VolunteerDetails[] = [];
  encryptedId: string | null = null;
  router: Router;
  isEditMode: boolean = false;
  editableVolunteer: VolunteerDetails | null = null;
  resumeFile: File | null = null;

  constructor(private appService: AppService,
    router: Router,
    private route: ActivatedRoute,
    private toaster: ToastrService,
    private spinner: NgxSpinnerService,
    private encryptionService: EncryptionService
  ) { 
    this.router = router;
  }

ngOnInit(): void {
  const encryptedIdParam = this.route.snapshot.paramMap.get('id');
  this.encryptedId = encryptedIdParam;

  if (encryptedIdParam) {
    try {
      const decryptedId = this.encryptionService.decrypt(decodeURIComponent(encryptedIdParam));
      this.loadVolunteerDetail(Number(decryptedId));
    } catch (error) {
      console.error('Error decrypting volunteer ID:', error);
      this.loadVolunteers();
    }
  } else {
    this.loadVolunteers();
  }
}

  previewUploadedFile(volunteerId: number) {
    if (!volunteerId) return;
    this.spinner.show();  

    this.appService.getVolunteerResume(volunteerId).subscribe({
      next: (fileArrayBuffer: ArrayBuffer) => {
        let fileType = 'application/octet-stream';
        const arr = new Uint8Array(fileArrayBuffer.slice(0, 4));
        const header = arr.reduce((acc, curr) => acc + curr.toString(16), '');

        if (header.startsWith('25504446')) {
          fileType = 'application/pdf';
        } else if (header.startsWith('504b34')) {
          fileType = 'application/vnd.openxmlformats-officedocument.wordprocessingml.document';
        } else if (header.startsWith('ffd8ff')) {
          fileType = 'image/jpeg';
        } else if (header.startsWith('89504e47')) {
          fileType = 'image/png';
        }

        const blob = new Blob([fileArrayBuffer], { type: fileType });
        const fileURL = URL.createObjectURL(blob);
        window.open(fileURL, '_blank');
        this.spinner.hide();
      },
      error: () => {
        this.toaster.error('Unable to load uploaded file');
        this.spinner.hide();
      }
    });
  }


  downloadAdminFile(volunteerId: number) {
    this.appService.getAdminUploadedFile(volunteerId).subscribe({
      next: (fileArrayBuffer: ArrayBuffer) => {
        let fileType = 'application/octet-stream';
        const arr = new Uint8Array(fileArrayBuffer.slice(0, 4));
        const header = arr.reduce((acc, curr) => acc + curr.toString(16), '');

        if (header.startsWith('25504446')) {
          fileType = 'application/pdf';
        } else if (header.startsWith('504b34')) {
          fileType = 'application/vnd.openxmlformats-officedocument.wordprocessingml.document';
        } else if (header.startsWith('ffd8ff')) {
          fileType = 'image/jpeg';
        } else if (header.startsWith('89504e47')) {
          fileType = 'image/png';
        }

        const blob = new Blob([fileArrayBuffer], { type: fileType });
        const fileURL = window.URL.createObjectURL(blob);
        const downloadLink = document.createElement('a');
        downloadLink.href = fileURL;
        downloadLink.download = `admin_upload_${volunteerId}${this.getFileExtension(fileType)}`;
        downloadLink.click();
        window.URL.revokeObjectURL(fileURL);
      },
      error: () => {
        this.toaster.error('Unable to download admin uploaded file');
      }
    });
  }

  private getFileExtension(fileType: string): string {
    switch (fileType) {
      case 'application/pdf': return '.pdf';
      case 'application/vnd.openxmlformats-officedocument.wordprocessingml.document': return '.docx';
      case 'image/jpeg': return '.jpeg';
      case 'image/png': return '.png';
      default: return '';
    }
  }

  loadVolunteerDetail(volunteerId: number): void {
    this.spinner.show();
    this.appService.getVolunteerById(volunteerId).subscribe({
      next: (data) => {
        this.volunteers = [data];
      },
      error: (err) => {
        console.error('Error loading volunteer detail:', err);
        this.toaster.error('Unable to load volunteer details');
        this.router.navigate(['/main/volunteer']);
      },
      complete: () => {
        setTimeout(() => this.spinner.hide(), 2000);
      },
    });
  }

  loadVolunteers(): void {
    this.spinner.show();

    this.appService.getAllVolunteers().subscribe({
      next: (data) => {
        this.volunteers = data.sort((a: any, b: any) => b.volunteerDetailsSeq - a.volunteerDetailsSeq);
      },
      error: (err) => {
        console.error('Error loading volunteers:', err);
      },
      complete: () => {
        setTimeout(() => this.spinner.hide(), 2000);
      },
    });
  }

viewVolunteer(id: number): void {
  const encryptedId = this.encryptionService.encrypt(id.toString());
  this.router.navigate(['/main/volunteer', encodeURIComponent(encryptedId)]);
}

toggleEditMode(): void {
  this.isEditMode = !this.isEditMode;
  if (this.isEditMode && this.volunteers[0]) {
    this.editableVolunteer = { ...this.volunteers[0] };
  } else {
    this.editableVolunteer = null;
  }
}

saveChanges(): void {
  if (!this.editableVolunteer) return;

  this.spinner.show();
  
  // If there's a resume file to upload
  if (this.resumeFile) {
    this.uploadAdminFile();
  } else {
    this.updateVolunteerDetails();
  }
}

uploadAdminFile(): void {
  if (!this.resumeFile || !this.editableVolunteer) return;

  const reader = new FileReader();

  reader.onload = () => {
    const base64 = (reader.result as string).split(',')[1];

    const payload = {
      volunteerId: this.editableVolunteer!.volunteerDetailsSeq,
      fileData: base64,
      fileType: this.resumeFile!.type
    };

    this.appService.updateVolunteerResume(payload).subscribe({
      next: () => {
        this.updateVolunteerDetails();
      },
      error: (err) => {
        console.error('Admin Upload Failed:', err);
        this.toaster.error('Admin file upload failed');
        this.spinner.hide();
      }
    });
  };

  reader.readAsDataURL(this.resumeFile);
}


private updateVolunteerDetails(): void {
  if (!this.editableVolunteer) return;

  // Update the volunteer in the local array
  this.volunteers[0] = { ...this.editableVolunteer };
  this.isEditMode = false;
  this.editableVolunteer = null;
  this.resumeFile = null;
  
  this.toaster.success('Volunteer details updated successfully');
  setTimeout(() => this.spinner.hide(), 1000);
}

onResumeFileSelected(event: any): void {
  const file = event.target.files[0];
  if (file) {
    this.resumeFile = file;
  }
}

cancelEdit(): void {
  this.isEditMode = false;
  this.editableVolunteer = null;
}

}
